import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { groupBy } from 'lodash'
import moment from 'moment';
import { Filesystem } from '@capacitor/core';

interface WarehousesDoneListState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };

    documents: any[];
    days: any[];
    schema: any;

    meta: any;
}

const state: WarehousesDoneListState = {
    loading: false,
    loaded: false,
    fatalError: false,

    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },

    documents: [],
    days: [],
    schema: null,

    meta: {},
};

export const ITEMS_LIST = 'GET_WAREHOUSE_DONE_LIST';
export const ITEMS_LIST_CUSTOM = 'GET_WAREHOUSE_DONE_LIST_CUSTOM';

export const actions: ActionTree<WarehousesDoneListState, RootState> = {
    loadData({ commit, getters, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('CLEAR_DATA');
            commit('START_LOADING');

            const columns: any = {
                'id': 'sacpm.id',
                'plan_id': 'sacp.id',
                'shift_id': 'sacp.shift_id',
                'carantine_start_date': 'sacp.carantine_start_date',
                'carantine_end_date': 'sacp.carantine_end_date',
                'carantine_comment': 'sacp.carantine_comment',
                'number': 'sacp.number',
                'fact_container_name': 'pc.name',
                'fact_container_weight': 'vcf.weight',
                'product_name': 'p.name',
                'container_weight': 'pc.weight',
                'quantity': 'sfa.quantity',
                'fact_weight': 'sfa.fact_weight',
                'shift_date': 's.shift_date',
                'set_fan_date': 'sja.set_fan_date',
                'total': 'sacpm.total',
                'checks': 'sacl.checks',
                'consistency_quarantine': 'vc.quarantine',
                'density_quarantine': 'vd.quarantine',
                'quality_pps_quarantine': 'vqp.quarantine',
                'stirability_thermomix_quarantine': 'voc1.quarantine',
                'stirability_thermomix_recycling': 'voc1.recycling',
                'glitter_thermomix_quarantine': 'voc2.quarantine',
                'glitter_thermomix_recycling': 'voc2.recycling',
                'thermal_stability_quarantine': 'voc3.quarantine',
                'thermal_stability_recycling': 'voc3.recycling',
                'heat_resistance_quarantine': 'voc4.quarantine',
                'heat_resistance_recycling': 'voc4.recycling',
                'hardness_quarantine': 'voc5.quarantine',
                'hardness_recycling': 'voc5.recycling',
                'causticity_initial_spalling_quarantine': 'voc6.quarantine',
                'causticity_initial_spalling_recycling': 'voc6.recycling',
                'plasticity_quarantine': 'voc7.quarantine',
                'plasticity_recycling': 'voc7.recycling',
                'looseness_quarantine': 'voc8.quarantine',
                'looseness_recycling': 'voc8.recycling',
                'sound_product_breaking_quarantine': 'voc9.quarantine',
                'sound_product_breaking_recycling': 'voc9.recycling',
                'sound_product_released_quarantine': 'voc10.quarantine',
                'sound_product_released_recycling': 'voc10.recycling',
                'feeling_of_texture_quarantine': 'voc11.quarantine',
                'feeling_of_texture_recycling': 'voc11.recycling',
                'fluff_effect_quarantine': 'voc12.quarantine',
                'fluff_effect_recycling': 'voc12.recycling',
                'kislinka_quarantine': 'voc13.quarantine',
                'kislinka_recycling': 'voc13.recycling',
                'serbian_profile_quarantine': 'voc14.quarantine',
                'serbian_profile_recycling': 'voc14.recycling',
                'sweetness_quarantine': 'voc15.quarantine',
                'sweetness_recycling': 'voc15.recycling',
                'salinity_quarantine': 'voc16.quarantine',
                'salinity_recycling': 'voc16.recycling',
                'vices_quarantine': 'voc17.quarantine',
                'vices_recycling': 'voc17.recycling',
                'exported_quantity': 'saetc.quantity',
                'exported_weight': 'saetc.weight',
                'product_container_id': 'sa.product_container_id',
            };
            const custom_columns: any = {
              id: 'scf.id',
              plan_id: 'scp.id',
              shift_id: 'scp.shift_id',
              number: 'scp.number',
              product_name: 'p.name',
              fact_container_name: 'pc.name',
              product_container_id: 'pc.id',
              quantity: 'scf.quantity',
              shift_date: 's.shift_date',
              container_weight: 'pc.weight',
              exported_quantity: 'scetc.quantity',
              exported_weight: 'scetc.weight',
            }

            const filter: any[] = [];

            filter.push({ "$eq": ["sacp.state", {"$": 'done'}]})
            filter.push({ "$eq": ["sacp.is_gone", {"$": false}]})

            const atom_sort = columns[params.sortField]? columns[params.sortField] : 's.shift_date';
            const custom_sort = custom_columns[params.sortField]? custom_columns[params.sortField] : 's.shift_date';

            vm.$db([
                vm.$q(ITEMS_LIST)
                    .raw('documents.list')
                    .action({
                        "columns": columns,
                        "from": "s:shift_atom_coock_plan",
                        "from_as": "sacp",
                        "joins": [
                            {
                                "join": "s:shifts",
                                "type": "left",
                                "as": "s",
                                "on": {
                                    $eq: ["s.id", "sacp.shift_id"]
                                }
                            },
                            {
                                "join": "s:shift_journal_atom",
                                "type": "left",
                                "as": "sja",
                                "on": {
                                    $eq: ["sja.plan_id", "sacp.id"]
                                }
                            },
                            {
                                "join": "s:voc_consistencies",
                                "type": "left",
                                "as": "vc",
                                "on": {
                                    $eq: ["vc.id", "sja.consitency_id"]
                                }
                            },
                            {
                                "join": "s:voc_densities",
                                "type": "left",
                                "as": "vd",
                                "on": {
                                    $eq: ["vd.id", "sja.density_id"]
                                }
                            },
                            {
                                "join": "s:voc_quality_pps",
                                "type": "left",
                                "as": "vqp",
                                "on": {
                                    $eq: ["vqp.id", "sja.quality_id"]
                                }
                            },
                            {
                                "join": "s:shift_atom_coock_plan_marks",
                                "type": "left",
                                "as": "sacpm",
                                "on": {
                                    $eq: ["sacpm.plan_id", "sacp.id"]
                                }
                            },
                            {
                                "join": "s:shift_atom",
                                "type": "left",
                                "as": "sa",
                                "on": {
                                    $eq: ["sa.coock_plan_id", "sacp.id"]
                                }
                            },
                            {
                                "join": "s:shift_fact_atom",
                                "type": "left",
                                "as": "sfa",
                                "on": {
                                    $eq: ["sfa.plan_id", "sa.id"]
                                }
                            },
                            {
                              join: 's:voc_containers',
                              type: 'left',
                              as:   'vcf',
                              on: {
                                $eq: ['sfa.container_id', 'vcf.id']
                              }
                            },
                            {
                              join: 's:products',
                              type: 'left',
                              as:   'p',
                              on: {
                                $eq: ['sa.product_id', 'p.id']
                              }
                            },
                            {
                              join: 's:product_containers',
                              type: 'left',
                              as:   'pc',
                              on: {
                                $eq: ['sa.product_container_id', 'pc.id']
                              }
                            },
                            {
                              join: 's:shift_atom_laboratory',
                              type: 'left',
                              as:   'sal',
                              on: {
                                $eq: ['sal.plan_id', 'sacp.id']
                              }
                            },
                            {
                                join: 's:voc_stirability_thermomix',
                                type: 'left',
                                as:   'voc1',
                                on: {
                                  $eq: ['sal.stirability_thermomix_id', 'voc1.id']
                                }
                              },
                              {
                                join: 's:voc_glitter_thermomix',
                                type: 'left',
                                as:   'voc2',
                                on: {
                                  $eq: ['sal.glitter_thermomix_id', 'voc2.id']
                                }
                              },
                              {
                                join: 's:voc_thermal_stability',
                                type: 'left',
                                as:   'voc3',
                                on: {
                                  $eq: ['sal.thermal_stability_id', 'voc3.id']
                                }
                              },
                              {
                                join: 's:voc_heat_resistance',
                                type: 'left',
                                as:   'voc4',
                                on: {
                                  $eq: ['sal.heat_resistance_id', 'voc4.id']
                                }
                              },
                              {
                                join: 's:voc_hardness',
                                type: 'left',
                                as:   'voc5',
                                on: {
                                  $eq: ['sal.hardness_id', 'voc5.id']
                                }
                              },
                              {
                                join: 's:voc_causticity_initial_spalling',
                                type: 'left',
                                as:   'voc6',
                                on: {
                                  $eq: ['sal.causticity_initial_spalling_id', 'voc6.id']
                                }
                              },
                              {
                                join: 's:voc_plasticity',
                                type: 'left',
                                as:   'voc7',
                                on: {
                                  $eq: ['sal.plasticity_id', 'voc7.id']
                                }
                              },
                              {
                                join: 's:voc_looseness',
                                type: 'left',
                                as:   'voc8',
                                on: {
                                  $eq: ['sal.looseness_id', 'voc8.id']
                                }
                              },
                              {
                                join: 's:voc_sound_product_breaking',
                                type: 'left',
                                as:   'voc9',
                                on: {
                                  $eq: ['sal.sound_product_breaking_id', 'voc9.id']
                                }
                              },
                              {
                                join: 's:voc_sound_product_released',
                                type: 'left',
                                as:   'voc10',
                                on: {
                                  $eq: ['sal.sound_product_released_id', 'voc10.id']
                                }
                              },
                              {
                                join: 's:voc_feeling_of_texture',
                                type: 'left',
                                as:   'voc11',
                                on: {
                                  $eq: ['sal.feeling_of_texture_id', 'voc11.id']
                                }
                              },
                              {
                                join: 's:voc_fluff_effect',
                                type: 'left',
                                as:   'voc12',
                                on: {
                                  $eq: ['sal.fluff_effect_id', 'voc12.id']
                                }
                              },
                              {
                                join: 's:voc_kislinka',
                                type: 'left',
                                as:   'voc13',
                                on: {
                                  $eq: ['sal.kislinka_id', 'voc13.id']
                                }
                              },
                              {
                                join: 's:voc_serbian_profile',
                                type: 'left',
                                as:   'voc14',
                                on: {
                                  $eq: ['sal.serbian_profile_id', 'voc14.id']
                                }
                              },
                              {
                                join: 's:voc_sweetness',
                                type: 'left',
                                as:   'voc15',
                                on: {
                                  $eq: ['sal.sweetness_id', 'voc15.id']
                                }
                              },
                              {
                                join: 's:voc_salinity',
                                type: 'left',
                                as:   'voc16',
                                on: {
                                  $eq: ['sal.salinity_id', 'voc16.id']
                                }
                              },
                              {
                                join: 's:voc_vices',
                                type: 'left',
                                as: 'voc17',
                                on: {
                                  $eq: ['sal.vices_id', 'voc17.id']
                                }
                              },
                              {
                                join: {
                                  columns: {
                                    plan_id: 'sacl.plan_id',
                                    checks: {
                                        $array_agg: {
                                            $obj: {
                                                check_1: 'sacl.check_1',
                                                check_2: 'sacl.check_2',
                                                check_3: 'sacl.check_3',
                                                check_4: 'sacl.check_4',
                                                check_5: 'sacl.check_5',
                                                check_6: 'sacl.check_6',
                                                check_7: 'sacl.check_7',
                                                check_8: 'sacl.check_8',
                                                check_9: 'sacl.check_9',
                                                check_10: 'sacl.check_10',
                                                check_11: 'sacl.check_11',
                                            }
                                        }
                                    }
                                  },
                                  from: 's:shift_atom_check_lists',
                                  from_as: 'sacl',
                                  sort: [
                                    {$desc: 'sacl.id'}
                                  ],
                                  groups: [
                                    'sacl.plan_id'
                                  ]
                                },
                                type: 'left',
                                as: 'sacl',
                                on: { $eq: ['sa.id', 'sacl.plan_id'] }
                              },
                              {
                                join: {
                                  columns: {
                                    plan_id: 'saetc.shift_atom_plan_id',
                                    product_container_id: 'saetc.product_container_id',
                                    quantity: {$sum: 'saetc.quantity'},
                                    weight: {$sum: 'saetc.weight'},
                                  },
                                  from: 's:shift_atom_exported_to_crm',
                                  from_as: 'saetc',
                                  groups: [
                                    'saetc.shift_atom_plan_id',
                                    'saetc.product_container_id'
                                  ]
                                },
                                type: 'left',
                                as: 'saetc',
                                on: {
                                  $and: [
                                    {$eq: ['saetc.plan_id', 'sacp.id']},
                                    {$eq: ['saetc.product_container_id', 'pc.id']}
                                  ]
                                }
                              }
                        ],
                        page_size: 10000,
                        ...(
                            filter.length > 1 ?
                            {
                                "where": {
                                    "$and": filter
                                }
                            } : {
                                "where": filter[0]
                            }
                        ),
                        order: [
                          ...[params.sortDirection == 'desc' ? {$desc: ['s.shift_date']} :  {$asc: ['s.shift_date']}],
                          ...[params.sortDirection == 'desc' ? {$desc: [atom_sort]} :  {$asc: [atom_sort]}],
                        ],
                    }),
                    vm.$q('SCHEMA')
                        .raw('schemas.get')
                        .action({
                            id: `s:shift_atom_coock_plan_marks`
                        }),
                    vm.$q(ITEMS_LIST_CUSTOM)
                        .raw('documents.list')
                        .action({
                          columns: custom_columns,
                          from: 's:shift_custom_plan',
                          from_as: 'scp',
                          joins: [
                            {
                                "join": "s:shifts",
                                "type": "left",
                                "as": "s",
                                "on": {
                                    $eq: ["s.id", "scp.shift_id"]
                                }
                            },
                            {
                              join: 's:shift_custom_fact',
                              type: 'left',
                              as: 'scf',
                              on: {
                                $eq: ['scf.plan_id', 'scp.id']
                              }
                            },
                            {
                              join: 's:products',
                              type: 'left',
                              as: 'p',
                              on: {
                                $eq: ['scp.product_id', 'p.id']
                              }
                            },
                            {
                              join: 's:product_containers',
                              type: 'left',
                              as: 'pc',
                              on: {
                                $eq: ['scf.product_container_id', 'pc.id']
                              }
                            },
                            {
                              join: {
                                columns: {
                                  plan_id: 'scetc.shift_custom_plan_id',
                                  product_container_id: 'scetc.product_container_id',
                                  quantity: {$sum: 'scetc.quantity'},
                                  weight: {$sum: 'scetc.weight'},
                                },
                                from: 's:shift_custom_exported_to_crm',
                                from_as: 'scetc',
                                groups: [
                                  'scetc.shift_custom_plan_id',
                                  'scetc.product_container_id'
                                ]
                              },
                              type: 'left',
                              as: 'scetc',
                              on: {
                                  $and: [
                                    {$eq: ['scetc.plan_id', 'scp.id']},
                                    {$eq: ['scetc.product_container_id', 'pc.id']}
                                  ]
                                }
                            }
                              
                          ],
                          page_size: 10000,
                          where: {
                            $and: [
                              {$eq: [`scf.is_done`, {$: true}]},
                              {$eq: [`scp.state`, {$: 'done'}]},
                              {$eq: [`scp.is_gone`, {$: false}]},
                            ]
                          },
                          order: [
                            ...[params.sortDirection == 'desc' ? {$desc: ['s.shift_date']} :  {$asc: ['s.shift_date']}],
                            ...[params.sortDirection == 'desc' ? {$desc: [custom_sort]} :  {$asc: [custom_sort]}],
                          ],
                        })
            ]).then(((result: ServerResult) => {
                commit('SET_WAREHOUSE_DATA', {
                    data: result.getOperation(ITEMS_LIST).getContent(), 
                    data_custom: result.getOperation(ITEMS_LIST_CUSTOM).getContent(),
                    schema: result.getOperation('SCHEMA').getContent().schema,
                });
                commit('LOADED_SUCCESS');

                resolve();
            }));
        })
    },

};

export const mutations: MutationTree<WarehousesDoneListState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },
    CLEAR_DATA(state, data) {
        state.days = []
        state.documents =[]
    },

    SET_WAREHOUSE_DATA(state, {data, data_custom, schema}) {
        state.documents = data.documents.map((item: any) => {
            const checks = item.checks !== null && item.checks.length > 0 ? (
                item.checks[0].check_1 || item.checks[0].check_2 ? false : true
            ) : null
            
            const boiling = item.consistency_quarantine || item.density_quarantine || item.quality_pps_quarantine
            const gpx = item.stirability_thermomix_quarantine || 
                                item.stirability_thermomix_recycling || 
                                item.glitter_thermomix_quarantine || 
                                item.glitter_thermomix_recycling || 
                                item.thermal_stability_quarantine || 
                                item.thermal_stability_recycling || 
                                item.heat_resistance_quarantine || 
                                item.heat_resistance_recycling || 
                                item.hardness_quarantine || 
                                item.hardness_recycling || 
                                item.causticity_initial_spalling_quarantine || 
                                item.causticity_initial_spalling_recycling || 
                                item.plasticity_quarantine || 
                                item.plasticity_recycling || 
                                item.looseness_quarantine || 
                                item.looseness_recycling || 
                                item.sound_product_breaking_quarantine || 
                                item.sound_product_breaking_recycling || 
                                item.sound_product_released_quarantine || 
                                item.sound_product_released_recycling || 
                                item.feeling_of_texture_quarantine || 
                                item.feeling_of_texture_recycling || 
                                item.fluff_effect_quarantine || 
                                item.fluff_effect_recycling || 
                                item.kislinka_quarantine || 
                                item.kislinka_recycling || 
                                item.serbian_profile_quarantine || 
                                item.serbian_profile_recycling || 
                                item.sweetness_quarantine || 
                                item.sweetness_recycling || 
                                item.salinity_quarantine || 
                                item.salinity_recycling || 
                                item.vices_quarantine || 
                                item.vices_recycling

            item.class_name = checks && !boiling && !gpx ? 'clot-ok' : 'clot-warn'
            item.checks = checks
            item.boiling = boiling
            item.gpx = gpx
            item.fact_weight = parseFloat(item.fact_weight)
            item.exported_weight = parseFloat(item.exported_weight) || 0
            item.line_template = 'atom'
            item.remains_quantity = item.quantity - item.exported_quantity
            item.remains_weight = Math.round((item.fact_weight - item.exported_weight) * 100) / 100
            return item
        })

        state.documents.push(...data_custom.documents.map((item: any) => {
            item.fact_weight = parseFloat(item.container_weight) * item.quantity
            item.exported_weight = parseFloat(item.exported_weight) || 0
            item.line_template = 'custom'
            item.remains_quantity = item.quantity - item.exported_quantity
            item.remains_weight = Math.round((item.fact_weight - item.exported_weight) * 100) / 100
            return item
        }))


        state.meta = fetchEnumsFromSchema(schema);

        state.lastMeta = {
            page: data.meta.page,
            pageSize: data.meta.page_size,
            totalCount: data.meta.total_count,
            totalPages: data.meta.total_pages
        };
    },
};

const getters = {
    total(state: WarehousesDoneListState) {
        return {
            quantity: state.documents.reduce((prev: any, field: any) => {
                return prev + parseInt(field.quantity)
            }, 0),
            total_quantity: state.documents.reduce((prev: any, field: any) => {
                return prev + (parseInt(field.quantity) * parseFloat(field.product_container_weight))
            }, 0),
            columns:  state.documents.reduce((prev: any, field: any) => {
                field.columns.map((item: any, idx: number) => {
                    console.log('=>', item, idx)
                    if (!prev[idx]) {
                        prev[idx] = {
                            'expected': 0,
                            'remains': 0,
                            'availability': 0,
                            'remains_total': 0,
                        }
                    }
                    prev[idx].expected += parseFloat(item.expected)
                    prev[idx].remains += parseFloat(item.remains)
                    prev[idx].availability += parseFloat(item.availability)
                    prev[idx].remains_total += parseFloat(item.remains_total)
                })
                return prev
            }, [])
        }
    },
    exportData(state: WarehousesDoneListState) {
        const csvContent: any[] = [];
        const header = ['База', 'Тара', 'Кол-во, кг', 'Кол-во ведер', ]

        state.documents.map((item: any) => {
            const row: any[] = [];
            row.push(item.base_name)
            row.push(item.container_name)
            row.push(item.weight)
            row.push(item.quantity)
            csvContent.push(row)
        })
        return [
            header,
            ...csvContent
        ]
    },
    groupedRows: (state: WarehousesDoneListState) => {
      const docs = state.documents.reduce((prev: any, item: any) => {
        if (!prev[item.shift_date]) {
          prev[item.shift_date] = []
        }
        prev[item.shift_date].push(item)
        return prev
      }, {})
      return Object.keys(docs).reduce((acc: any[], key: any) => {
        acc.push({
          header: true,
          shift_date: key,
            quantity: docs[key].reduce((c: number, i: any) => {
                c += parseInt(i.quantity) || 0; 
                return c;
            }, 0),
            fact_weight: docs[key].reduce((c: number, i: any) => {
                c += parseFloat(i.fact_weight) || 0; 
                return c;
            }, 0),
            exported_quantity: docs[key].reduce((c: number, i: any) => {
                c += parseInt(i.exported_quantity) || 0; 
                return c;
            }, 0),
            exported_weight: docs[key].reduce((c: number, i: any) => {
                c += parseFloat(i.exported_weight) || 0; 
                return c;
            }, 0),
            remains_quantity: docs[key].reduce((c: number, i: any) => {
                c += parseInt(i.remains_quantity) || 0; 
                return c;
            }, 0),
            remains_weight: docs[key].reduce((c: number, i: any) => {
                c += parseFloat(i.remains_weight) || 0; 
                return c;
            }, 0)
        })
        acc.push(...docs[key])
        return acc
      }, []) 
    }
};

export const STORE_KEY = '$_warehouses_done_list';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
